import React, { useState, useEffect, useRef, useCallback } from "react";
import { useSelector, useDispatch } from "react-redux";
import Loader, { LocalStorage } from "../../../utils/Utils";
import Logger from "../../../utils/Logger";
import { jsPDF } from "jspdf";
import "../styles/CertificateGenerator.scss";
import { useApi } from "../../../service/ApiContext";
import { RootState } from "../../../store/store";
import { ProgramDetails } from "../../calender/data/redux/action/ProgramDetailAction";
import { setPrograms } from "../../calender/data/redux/reducer/ProgramDetailReducer";
import { Box, Button, MenuItem, Popover } from "@mui/material";
import DropDown from "../../learning_room/assets/images/Dropdownicon.png";
import GreyHorizontalDivider from "../../learning_room/components/GreyDividerComponent";
import download from "../../../assets/images/certificates/download 1 (1).svg";
import warning from "../../../assets/images/certificates/Group (1).svg";

interface Program {
  id: string;
  name: string;
  [key: string]: any;
}

// interface CertificateResponse {
//   certificate_url: string;
//   detail?: string | null;
//   error?: string | null;
// }

interface CertificateResponse {
  certificate_url:
    | string
    | {
        status_code: number;
        background: string | null;
        body: string;
        raw_headers?: string[][];
      };

  detail?: string | null;
  error?: string | null;
}

const CertificateGenerator: React.FC = () => {
  const dispatch = useDispatch();
  const api = useApi();

  const userData = useSelector((state: RootState) => state.profile.profileKeys);
  const programs = useSelector((state: RootState) => state.programs.programs);

  const [selectedProgramId, setSelectedProgramId] = useState<string>("");
  const [certificateImageUrl, setCertificateImageUrl] = useState<string | null>(
    null,
  );
  const [isLoadingCert, setIsLoadingCert] = useState<boolean>(false);
  const [certError, setCertError] = useState<string | null>(null);
  const [isGeneratingPdf, setIsGeneratingPdf] = useState<boolean>(false);
  const [anchorEl2, setAnchorEl2] = useState<HTMLElement | null>(null);

  const hasFetchedPrograms = useRef<boolean>(false);

  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const studentName = userData?.first_name
    ? `${userData.first_name} ${userData.last_name || ""}`.trim()
    : "Valued Student";

  const getPrograms = useCallback(async () => {
    Loader.showLoading("Loading");
    api.setAuthToken(LocalStorage.getLocalData("access_token"));
    const institute_id = LocalStorage.getLocalData("institute_id");

    try {
      const response = await api.get<any>(`/eprogram/${institute_id}`);
      Logger.info("programs response", response.data.programs);

      if (response.data.error) {
        dispatch(setPrograms([]));
        return;
      }

      if (response.data.programs && response.data.programs.length > 0) {
        dispatch(setPrograms(response.data.programs));
        Logger.info("redux data program", response.data.programs);
      } else {
        dispatch(setPrograms([]));
      }
    } catch (error) {
      Logger.info("error response", error);
      dispatch(setPrograms([]));
    } finally {
      Loader.dismissLoading();
    }
  }, [api, dispatch]);

  const fetchCertificate = useCallback(
    async (programId: string) => {
      if (!programId) {
        setCertificateImageUrl(null);
        setCertError(null);
        return;
      }

      setIsLoadingCert(true);
      setCertError(null);
      api.setAuthToken(LocalStorage.getLocalData("access_token"));

      try {
        const response = await api.get<CertificateResponse>(`/certificate`, {
          program_id: programId,
        });

        Logger.info("certificate response", response.data);

        // if (response.data?.certificate_url) {
        //   setCertificateImageUrl(response.data.certificate_url);
        //   setCertError(null);
        // } else {
        //   setCertificateImageUrl(null);
        //   setCertError(
        //     response.data?.error || "Certificate template not available",
        //   );
        // }
        if (
          response.data?.certificate_url &&
          typeof response.data.certificate_url === "object" &&
          response.data.certificate_url.body
        ) {
          const parsedBody = JSON.parse(response.data.certificate_url.body);

          setCertificateImageUrl(null);
          setCertError(
            parsedBody?.error || "Certificate template not available",
          );
        } else if (
          response.data?.certificate_url &&
          typeof response.data.certificate_url === "string"
        ) {
          setCertificateImageUrl(response.data.certificate_url);
          setCertError(null);
        } else {
          setCertificateImageUrl(null);
          setCertError("Certificate template not available");
        }
      } catch (error: any) {
        Logger.info("certificate error", error);
        setCertificateImageUrl(null);
        if (error?.response?.data?.message) {
          setCertError(error.response.data.message);
        } else if (error?.message) {
          setCertError(error.message);
        } else {
          setCertError("Failed to load certificate. Please try again.");
        }
      } finally {
        setIsLoadingCert(false);
      }
    },
    [api],
  );

  const handleProgramChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const programId = e.target.value;
    setSelectedProgramId(programId);
    fetchCertificate(programId);
  };

  const drawCertificateWithName = useCallback(async (): Promise<
    string | null
  > => {
    if (!certificateImageUrl || !canvasRef.current) return null;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    if (!ctx) return null;

    return new Promise((resolve) => {
      const img = new Image();
      img.crossOrigin = "Anonymous";
      img.onload = () => {
        canvas.width = img.width;
        canvas.height = img.height;

        ctx.clearRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

        // Calculate font size based on name length and canvas width
        const nameLength = studentName.length;
        let fontSize = Math.min(canvas.width * 0.045, 72);

        // Adjust font size for longer names
        if (nameLength > 25) {
          fontSize = Math.min(canvas.width * 0.035, 60);
        } else if (nameLength > 15) {
          fontSize = Math.min(canvas.width * 0.04, 65);
        }

        // Set text style for the name
        ctx.font = `italic 600 ${fontSize}px "Times New Roman", "Georgia", serif`;
        ctx.fillStyle = "#a15c12";
        ctx.textAlign = "center";
        ctx.shadowColor = "rgba(0,0,0,0.1)";
        ctx.shadowBlur = 2;

        const xPos = canvas.width / 2;
        const yPos = canvas.height * 0.54;

        // Handle long names by splitting into multiple lines if needed
        const maxCharsPerLine = 35;
        if (studentName.length > maxCharsPerLine) {
          const words = studentName.split(" ");
          let lines: string[] = [];
          let currentLine = "";

          words.forEach((word) => {
            if ((currentLine + " " + word).length <= maxCharsPerLine) {
              currentLine = currentLine ? currentLine + " " + word : word;
            } else {
              if (currentLine) lines.push(currentLine);
              currentLine = word;
            }
          });
          if (currentLine) lines.push(currentLine);

          const lineHeight = fontSize * 1.2;
          const startY = yPos - ((lines.length - 1) * lineHeight) / 2;

          lines.forEach((line, index) => {
            ctx.fillText(line, xPos, startY + index * lineHeight);
          });
        } else {
          ctx.fillText(studentName, xPos, yPos);
        }

        // Add date
        ctx.font = `normal 14px "Times New Roman", "Georgia", serif`;
        ctx.fillStyle = "#2c1810";
        ctx.shadowBlur = 0;
        const dateY = canvas.height * 0.78;
        const currentDate = new Date().toLocaleDateString("en-US", {
          month: "long",
          day: "numeric",
          year: "numeric",
        });
        ctx.fillText(currentDate, xPos, dateY);

        resolve(canvas.toDataURL("image/png", 1.0));
      };
      img.onerror = () => {
        console.error("Failed to load certificate image");
        resolve(null);
      };
      img.src = certificateImageUrl;
    });
  }, [certificateImageUrl, studentName]);

  useEffect(() => {
    if (!certificateImageUrl || !canvasRef.current) return;

    const updatePreview = async () => {
      await drawCertificateWithName();
    };
    updatePreview();
  }, [certificateImageUrl, studentName, drawCertificateWithName]);

  const handleDownloadPDF = async () => {
    if (!certificateImageUrl) {
      alert("No certificate available to download");
      return;
    }

    setIsGeneratingPdf(true);
    try {
      const imageDataUrl = await drawCertificateWithName();
      if (!imageDataUrl) {
        alert("Failed to generate certificate image");
        return;
      }

      const canvas = canvasRef.current;
      if (!canvas) return;

      const pdf = new jsPDF({
        orientation: "landscape",
        unit: "mm",
        format: "a4",
      });

      const imgWidth = 297;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;

      pdf.addImage(imageDataUrl, "PNG", 0, 0, imgWidth, imgHeight);
      const fileName = `Certificate_${studentName.replace(/\s+/g, "_")}.pdf`;
      pdf.save(fileName);

      Logger.info("PDF downloaded successfully");
    } catch (error) {
      console.error("PDF generation error:", error);
      alert("Failed to generate PDF. Please try again.");
    } finally {
      setIsGeneratingPdf(false);
    }
  };

  useEffect(() => {
    if (!hasFetchedPrograms.current) {
      hasFetchedPrograms.current = true;
      getPrograms();
    }
  }, [getPrograms]);

  const renderPlaceholder = () => (
    <div className="certificates-placeholder">
      {!certError || isLoadingCert ? (
        <div className="certificates-placeholder-icon">
          <i className="fas fa-award"></i>
        </div>
      ) : (
        <></>
      )}
      {!certError || isLoadingCert ? (
        <h3 className="certificates-placeholder-title">Certificate Preview</h3>
      ) : (
        <h3>No Certificates Found</h3>
      )}
      <p className="certificates-placeholder-text">
        {!selectedProgramId
          ? "Select a program from the dropdown above to view the certificate"
          : isLoadingCert
            ? "Loading certificate template..."
            : certError
              ? "Complete a program to unlock your certificate"
              : "Certificate will appear here"}
      </p>
      {!selectedProgramId && (
        <div className="certificates-placeholder-illustration">
          <svg
            width="200"
            height="150"
            viewBox="0 0 200 150"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <rect
              x="20"
              y="20"
              width="160"
              height="110"
              rx="8"
              stroke="#d48806"
              strokeWidth="2"
              strokeDasharray="6 6"
              fill="#FFF8E7"
            />
            <text
              x="100"
              y="65"
              textAnchor="middle"
              fill="#a15c12"
              fontSize="14"
              fontFamily="sans-serif"
            >
              Certificate
            </text>
            <text
              x="100"
              y="85"
              textAnchor="middle"
              fill="#888"
              fontSize="12"
              fontFamily="sans-serif"
            >
              Preview Area
            </text>
            <path
              d="M70 105 L130 105"
              stroke="#d48806"
              strokeWidth="1.5"
              strokeDasharray="3 3"
            />
          </svg>
        </div>
      )}
      {isLoadingCert && (
        <div className="certificates-loading-spinner">
          <i className="fas fa-spinner fa-pulse"></i> Loading certificate...
        </div>
      )}
      {certError && !isLoadingCert && (
        <div className="certificates-error-message">
          <div>
            <img
              src={warning}
              alt=""
              style={{
                height: "24px",
                width: "24px",
              }}
            />
          </div>
          <div> {certError}</div>
        </div>
      )}
    </div>
  );

  return (
    <div className="certificates-generator">
      <div className="certificates-selection-panel">
        <div
          style={{
            color: "#000000",
            // marginLeft: "20px",
            fontWeight: "500",
            fontFamily: "Mazzard",
            fontSize: "20px",
            // marginRight: "20px", // Adjust margin to provide space after the welcome message
          }}
        >
          My Certificates
        </div>
        {/* <div className="certificates-program-selector">
          <label
            htmlFor="certificates-program-select"
            className="certificates-selector-label"
          >
            <i className="fas fa-graduation-cap"></i> Select Program
          </label>
          <select
            id="certificates-program-select"
            className="certificates-select-dropdown"
            value={selectedProgramId}
            onChange={handleProgramChange}
            disabled={isLoadingCert}
          >
            <option value="">Select a program</option>
            {programs &&
              programs.map((program: ProgramDetails) => (
                <option key={program.program_id} value={program.program_id}>
                  {program.program_title}
                </option>
              ))}
          </select>
        </div> */}
        <div className="program-dropdown">
          <div className="dropdown">
            <div
              className="dropdownw-select"
              onClick={(e) => setAnchorEl2(e.currentTarget)}
            >
              <p>
                {selectedProgramId
                  ? programs.find(
                      (p: ProgramDetails) =>
                        p.program_id.toString() === selectedProgramId,
                    )?.program_title
                  : "Select Program"}
              </p>

              <button
                className="dropdownchange-button"
                onClick={(e) => setAnchorEl2(e.currentTarget)}
              >
                Change
                <span>
                  <img
                    src={DropDown}
                    alt="dropdown"
                    style={{
                      width: "12px",
                      height: "8px",
                      marginLeft: "8px",
                    }}
                  />
                </span>
              </button>
            </div>

            <Popover
              open={Boolean(anchorEl2)}
              anchorEl={anchorEl2}
              onClose={() => setAnchorEl2(null)}
              anchorOrigin={{
                vertical: "bottom",
                horizontal: "left",
              }}
              transformOrigin={{
                vertical: "top",
                horizontal: "left",
              }}
              PaperProps={{
                style: {
                  width: "320px",
                  marginTop: "5px",
                  borderRadius: "12px",
                  backgroundColor: "#ffffff",
                  boxShadow: "0px 4px 20px rgba(0,0,0,0.12)",
                  padding: "8px",
                },
              }}
            >
              <Box sx={{ padding: "0px" }}>
                {programs && programs.length > 0 ? (
                  programs.map((program: ProgramDetails) => (
                    <MenuItem
                      key={program.program_id}
                      onClick={() => {
                        const programId = program.program_id.toString();

                        setSelectedProgramId(programId);
                        fetchCertificate(programId);
                      }}
                      style={{
                        fontFamily: "Mazzard",
                        color: "#242730",
                        fontSize: "16px",
                        fontWeight: "500",
                        borderRadius: "8px",
                        marginBottom: "4px",
                      }}
                    >
                      <span
                        style={{
                          whiteSpace: "normal",
                          wordBreak: "break-word",
                          width: "100%",
                        }}
                      >
                        {program.program_title}
                      </span>
                    </MenuItem>
                  ))
                ) : (
                  <MenuItem disabled>No programs found</MenuItem>
                )}
              </Box>
            </Popover>
          </div>
        </div>

        {/* <div className="certificates-student-info">
          <i className="fas fa-user-graduate"></i>
          <span className="certificates-student-name">
            <strong>Student:</strong> {studentName}
          </span>
        </div> */}
      </div>

      {/* <GreyHorizontalDivider /> */}

      <div className="certificates-preview-container" ref={containerRef}>
        {certificateImageUrl && !isLoadingCert && !certError ? (
          <div className="certificates-preview-wrapper">
            <div className="certificates-preview-header">
              <h3 className="certificates-preview-title">
                {/* <i className="fas fa-eye"></i> */}
                Certificate Preview
              </h3>
              {/* <button
                className="certificates-download-btn"
                onClick={handleDownloadPDF}
                disabled={isGeneratingPdf}
              >
                {isGeneratingPdf ? (
                  <>
                    <i className="fas fa-spinner fa-pulse"></i> Generating...
                  </>
                ) : (
                  <>
                    <i className="fas fa-download"></i> Download PDF
                  </>
                )}
              </button> */}
              <div
                style={{
                  backgroundColor: "#FFC85D",
                  width: "140px",
                  padding: "0px 5px 0px 5px",
                  borderRadius: "10px",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  height: "40px",
                  cursor: "pointer",
                  gap: "10px",
                }}
                onClick={handleDownloadPDF}
              >
                <img
                  src={download}
                  alt=""
                  style={{
                    height: "14px",
                    width: "14px",
                  }}
                />
                Download
              </div>
            </div>
            <div className="certificates-canvas-wrapper">
              <canvas
                ref={canvasRef}
                className="certificates-canvas"
                style={{
                  maxWidth: "100%",
                  height: "auto",
                  boxShadow: "0 20px 35px -10px rgba(0,0,0,0.2)",
                  borderRadius: "8px",
                  background: "#fff",
                }}
              />
            </div>
          </div>
        ) : (
          renderPlaceholder()
        )}
      </div>
    </div>
  );
};

export default CertificateGenerator;
